import { logEvent } from "../util/logEvent.ts";
import { toWorker } from "../util/toWorker.ts";
import i18n from "../util/i18n.ts";
import { helpers } from "../util/helpers.ts";
import { localizeNegotiationError } from "../util/localizeNegotiationError.ts";
import { getSignSuccessToastText } from "../util/successToast.ts";
import {
	getSigningHighlightCard,
	isBigSigning,
} from "../util/feedbackHeadline.ts";
import { emitHighlightCard } from "../util/highlightCard.ts";

// season is just needed during re-signing, because it's used to make sure drafted players in hard cap leagues always
// are willing to sign.
export const NegotiateButtons = ({
	canGoOverCap,
	capSpace,
	disabled,
	minContract,
	onNegotiate,
	season,
	spectator,
	p,
	resigning = false,
	willingToNegotiate,
}: {
	canGoOverCap?: boolean;
	capSpace: number;
	disabled?: boolean;
	minContract: number;
	onNegotiate: () => void;
	season: number;
	spectator: boolean;
	p: any;
	resigning?: boolean;
	willingToNegotiate: boolean;
}) => {
	if (spectator) {
		return null;
	}

	if (!willingToNegotiate) {
		return (
			<span data-negotiation-refuses-pid={p.pid}>
				{i18n.t("freeAgents.refuses", { ns: "ui", defaultValue: "Refuses!" })}
			</span>
		);
	}

	const contractAmount = p.mood.user.contractAmount;

	const signDisabled =
		!canGoOverCap &&
		(!!disabled ||
			(contractAmount / 1000 > capSpace + 1 / 1000 &&
				contractAmount / 1000 > (minContract + 1) / 1000));
	return (
		<div
			className="btn-group"
			data-negotiation-actions-pid={p.pid}
			data-negotiation-willing="true"
		>
			<button
				className="btn btn-light-bordered btn-xs"
				data-negotiation-open-pid={p.pid}
				disabled={!!disabled}
				onClick={onNegotiate}
			>
				{i18n.t("freeAgents.negotiate", { ns: "ui", defaultValue: "Negotiate" })}
			</button>
			<button
				className="btn btn-light-bordered btn-xs"
				data-negotiation-quick-sign-pid={p.pid}
				disabled={signDisabled}
				onClick={async () => {
					const errorMsg = await toWorker("main", "acceptContractNegotiation", {
						pid: p.pid,
						amount: contractAmount,
						exp: p.contract.exp,
					});

					if (errorMsg) {
						logEvent({
							type: "error",
							text: localizeNegotiationError(errorMsg),
							saveToDb: false,
						});
					}

					// N4-2/N4-1 success feedback: this quick-sign shortcut used to be
					// silent on success (only failures showed a red toast). A regular
					// signing fires the shared L1 success toast; a headline (star-level)
					// signing is promoted to an L2 highlight card *instead* (never both).
					// contractAmount is in thousands, so /1000 for the millions display;
					// the builder returns undefined on failure so the red toast above
					// stays the only feedback there.
					if (errorMsg === undefined) {
						const playerName = `${p.firstName} ${p.lastName}`;
						const years = Math.max(1, p.contract.exp - season + 1);
						const amountText = helpers.formatCurrency(
							contractAmount / 1000,
							"M",
						);
						if (isBigSigning(p.ratings?.ovr ?? 0)) {
							emitHighlightCard(
								getSigningHighlightCard({
									resigning,
									playerName,
									years,
									amountText,
									url: helpers.leagueUrl(["player", p.pid]),
								}),
							);
						} else {
							const successText = getSignSuccessToastText({
								errorMessage: errorMsg,
								resigning,
								playerName,
								years,
								amountText,
							});
							if (successText !== undefined) {
								logEvent({
									type: "success",
									text: successText,
									saveToDb: false,
								});
							}
						}
					}
				}}
			>
				{i18n.t("negotiation.sign", { ns: "ui", defaultValue: "Sign" })}
			</button>
		</div>
	);
};
